'use client';
import useStore, { StoreProps } from '@/app/[locale]/admin/store';
import { deleteHeaderItem } from '@/app/api/actions';
import ToastMessage from '@/components/ui/ToastMessage';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import React from 'react';
import { FaTrash } from 'react-icons/fa6';

export const EditableText: React.FC<{
    element: string;
    link: string;
    text: string;
    field: string;
    sectionId: number;
    blockId?: number;
    cellId?: number;
    htmlTag: string;
    locale: string;
}> = ({
    element,
    link,
    text,
    field,
    sectionId,
    blockId,
    cellId,
    htmlTag,
    locale,
}) => {
    const router = useRouter();
    const setEditedText = useStore((state: StoreProps) => state.setEditedText);
    const setEditedLink = useStore((state: StoreProps) => state.setEditedLink);

    const TiptapEditor = dynamic(
        () => import('@/components/ui/tiptap/TiptapEditor'),
        {
            ssr: false,
        },
    );

    const DeleteConfirm = dynamic(
        () => import('@/components/data/DeleteConfirm'),
        {
            ssr: false,
        },
    );

    const notify = React.useCallback((type: string, message: string) => {
        ToastMessage({ type, message });
    }, []);

    const onTextChange = (value: string | null) => {
        setEditedText({
            field: field,
            sectionId: sectionId,
            blockId: blockId || 0,
            cellId: cellId || 0,
            element: element,
            text: value || '',
            dirty: (value || '') !== (text || ''),
        });
    };

    const onLinkChange = (value: string | null) => {
        setEditedLink({
            field: field,
            sectionId: sectionId,
            blockId: blockId || 0,
            cellId: cellId || 0,
            element: element,
            text: value || '',
            dirty: (value || '') !== (text || ''),
        });
    };

    const editionType = htmlTag => {
        switch (htmlTag) {
            case 'h1':
            case 'h2':
            case 'h3':
            case 'h4':
            case 'h5':
            case 'h6':
            case 'div':
            case 'p':
                return 'divEditable';
            case 'html':
                return 'editor';
            case 'button':
                return 'button';
            default:
                return 'divEditable';
        }
    };

    return (
        <div className="flex flex-col items-center space-x-2">
            {
                {
                    divEditable: (
                        <div
                            className="min-w-full min-h-min px-2 pt-1 focus:border-primary focus:ring-primary focus:ring-offset-1 focus:ring-offset-primary border border-dashed border-primary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-dark"
                            contentEditable={true}
                            onBlur={e =>
                                onTextChange(e.currentTarget.innerText)
                            }
                            suppressContentEditableWarning={true}
                        >
                            {text.split('\n').map((line, index) => (
                                <React.Fragment key={index}>
                                    {line}
                                    <br />
                                </React.Fragment>
                            ))}
                        </div>
                    ),
                    editor: (
                        <TiptapEditor
                            handleEditorChange={value => onTextChange(value)}
                            initialValue={text}
                            locale={locale}
                        />
                    ),
                    button: (
                        <div className="flex">
                            <span className="pt-1 mr-1">link: </span>
                            <div
                                className="min-w-full px-2 pt-1 focus:border-primary focus:ring-primary focus:ring-offset-1 focus:ring-offset-primary border border-dashed border-primary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-dark"
                                contentEditable={true}
                                onBlur={e =>
                                    onLinkChange(e.currentTarget.textContent)
                                }
                                suppressContentEditableWarning={true}
                            >
                                {link}
                            </div>
                        </div>
                    ),
                }[editionType(htmlTag)]
            }
            <DeleteConfirm
                locale={locale}
                onConfirm={async () => {
                    const response = await deleteHeaderItem(element, sectionId);
                    if (response.success) {
                        router.refresh();
                    } else {
                        notify('error', response.message);
                    }
                }}
            >
                <button
                    className="ml-4 text-red-600 border border-red-600 hover:bg-red-700 hover:text-white focus:ring-4 focus:outline-none focus:ring-admin_variant font-medium rounded-md p-1.5 text-center inline-flex items-center"
                    type="button"
                >
                    <FaTrash className="h-6 w-6" />
                </button>
            </DeleteConfirm>
        </div>
    );
};
